import { Box, Checkbox, FormControl, FormControlLabel, FormLabel, InputAdornment, Radio, RadioGroup, TextField } from '@mui/material';
import { toBase64 } from 'js-base64';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from "react-hook-form";
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { RECORDING_DOMAIN, VTCLOUD_MARKETPLACE } from '../../../../../../modules/API/constants_ts';
import { createRecordingDialogEvent } from '../../../../analytics/AnalyticsEvents';
import { sendAnalytics } from '../../../../analytics/functions';
import axiosAuthHeader from '../../../../api/axiosAuthHeader';
import { IconClear, IconEyeHide, IconEyeShow } from '../../../../base/icons/svg';
import { JitsiRecordingConstants } from '../../../../base/lib-jitsi-meet';
import Tooltip from '../../../../base/tooltip/components/Tooltip';
import ClickableIcon from '../../../../base/ui/components/web/ClickableIcon';
import Dialog from '../../../../base/ui/components/web/Dialog';
import { FIELD_NAME, TOOLTIP_FIELD, VALIDATE_CODE } from '../../../../base/util/fieldConstants';
import { validateDifferent, validEncryptionKey } from '../../../../base/util/validate';
import { setCloseDialog } from '../../../../moderator-controller/slice';
import { showErrorNotification, showWarningNotification } from '../../../../notifications';
import { RECORDING_TYPES } from '../../../constants';
import { setAudioOnlyRecording } from '../../../actions.web';
import { getConfigValue } from '../../../../base/util/functions';
import { CONFIG_CODE } from '../../../../base/constants/default';
import { lowerCase } from 'lodash';
const useStyles = makeStyles()((theme) => {
    return {
        heightNone: {
            height: 0,
            overflow: 'hidden'
        },
        heightAuto: {
            height: '65px',
        }
    };
});
const TYPE_RECORD = {
    ENCRYPTION: 'encryption',
    NO: 'no'
};
const StartRecordingDialogV3 = () => {
    const { classes } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { getValues, setValue, handleSubmit, control, formState: { errors, isValid }, trigger } = useForm({ mode: "onChange" });
    const [isShowKey, setIsShowKey] = useState(false);
    const [isShowReKey, setIsShowReKey] = useState(false);
    const [audioOnly, setAudioOnly] = useState(false);
    const { roomInfo, conference, roomConfig } = useSelector((state) => state['features/base/conference']);
    const [typeRecord, setTypeRecord] = useState(TYPE_RECORD.NO);
    const [canEncrypt, setCanEncrypt] = useState(false);
    const [isCalled, setIsCalled] = useState(false);
    const isRecordAudioOnly = lowerCase(getConfigValue(CONFIG_CODE.RECORD_AUDIO_ONLY, roomConfig)) === "on";
    const onSubmit = async (data) => {
        let appData;
        const attributes = {};
        let record_id;
        try {
            const response = await axiosAuthHeader({
                method: "post",
                url: RECORDING_DOMAIN + `/start-recording`,
                data: {
                    room_name: roomInfo?.roomName,
                    room_id: roomInfo?.roomId
                }
            });
            record_id = response.record_id;
        }
        catch (error) {
            console.error(error);
            // Xử lý trường hợp lỗi khi không có dung lượng ghi hình
            if (error?.response?.status === 403) {
                dispatch(showWarningNotification({
                    titleKey: 'recording.noSpaceToRecord',
                    description: React.createElement(Trans, { i18nKey: "recording.noSpaceToRecordDescription" },
                        "this",
                        React.createElement("a", { style: { fontWeight: 500, color: '#C7002B', textDecoration: 'underline' }, href: VTCLOUD_MARKETPLACE, target: '_blank' }, "link")),
                    hideDismiss: true
                }));
            }
            else {
                dispatch(showErrorNotification({
                    descriptionKey: 'recording.error',
                    titleKey: 'recording.failedToStart'
                }));
            }
            return dispatch(setCloseDialog());
        }
        appData = JSON.stringify({
            'file_recording_metadata': {
                'share': true,
                'record_id': record_id,
                'record_encrypt_key': toBase64(data.encryptionKey),
                'audio_only': audioOnly ? 'true' : 'false'
            }
        });
        attributes.type = RECORDING_TYPES.JITSI_REC_SERVICE;
        sendAnalytics(createRecordingDialogEvent('start', 'confirm.button', attributes));
        isRecordAudioOnly && dispatch(setAudioOnlyRecording(audioOnly));
        // this._toggleScreenshotCapture();
        conference?.startRecording({
            mode: JitsiRecordingConstants.mode.FILE,
            appData
        });
    };
    const getEncryptionPermission = async () => {
        try {
            if (!isCalled)
                setIsCalled(false);
            const response = await axiosAuthHeader({
                method: "get",
                url: RECORDING_DOMAIN + `/get-encryption-permission`
            });
            setCanEncrypt(Boolean(response?.record_encryption_enable));
        }
        catch (e) {
            console.error(e);
        }
        finally {
            setIsCalled(true);
        }
    };
    useEffect(() => {
        getEncryptionPermission();
    }, []);
    return (React.createElement("form", { onSubmit: (e) => {
            e.preventDefault();
        } },
        React.createElement(Dialog, { titleKey: audioOnly ? 'dialog.startOnlyAudioRecording' : 'dialog.startRecording', onSubmit: handleSubmit(onSubmit), ok: {
                disabled: !isCalled || (!isValid && typeRecord === TYPE_RECORD.ENCRYPTION),
                translationKey: audioOnly ? 'dialog.startOnlyAudioRecording' : 'dialog.startRecording'
            }, hideTooltip: true, disableEnter: true },
            React.createElement(Box, null,
                React.createElement("p", null, audioOnly ? t('recording.serviceDescriptionAudioOnly') : t('recording.serviceDescription')),
                isRecordAudioOnly && React.createElement(FormControlLabel, { label: t('recording.audioOnly'), control: React.createElement(Checkbox, { onChange: (e) => setAudioOnly(e.target.checked), checked: audioOnly }) }),
                React.createElement(FormControl, { sx: { marginTop: '16px', transition: 'height 0.2s' }, className: `${classes.heightNone} ${canEncrypt && classes.heightAuto}` },
                    React.createElement(FormLabel, { required: true }, t('recording.encryption.labelSelect')),
                    React.createElement(RadioGroup, { defaultValue: TYPE_RECORD.NO, name: "type-record", value: typeRecord, onChange: (e) => setTypeRecord(e.target.value), sx: {
                            flexDirection: 'row',
                            columnGap: 40
                        } },
                        React.createElement(FormControlLabel, { sx: { fontSize: 16 }, value: TYPE_RECORD.ENCRYPTION, disabled: !canEncrypt, control: React.createElement(Radio, null), label: t('recording.encryption.encryption') }),
                        React.createElement(FormControlLabel, { sx: { fontSize: 16 }, value: TYPE_RECORD.NO, control: React.createElement(Radio, null), label: t('recording.encryption.no') })))),
            typeRecord === TYPE_RECORD.ENCRYPTION &&
                React.createElement(React.Fragment, null,
                    React.createElement(Controller, { control: control, name: 'encryptionKey', rules: {
                            validate: (value) => {
                                const _error = validEncryptionKey(value);
                                return _error === VALIDATE_CODE.OK || _error;
                            }
                        }, render: ({ field: { onChange, onBlur, value } }) => (React.createElement(FormControlLabel, { label: t('recording.encryption.key'), required: true, labelPlacement: 'top', control: React.createElement(TextField, { autoFocus: true, fullWidth: true, value: value, onChange: onChange, onKeyPress: (e) => e.key === 'Enter' && e.preventDefault(), placeholder: t('recording.encryption.placeholderKey'), onBlur: onBlur, type: isShowKey ? 'text' : 'password', error: errors.encryptionKey?.message !== VALIDATE_CODE.OK && errors.encryptionKey?.message !== undefined, helperText: errors.encryptionKey?.message !== undefined && errors.encryptionKey?.message !== VALIDATE_CODE.OK && t(errors.encryptionKey?.message, { field: t(FIELD_NAME.SECRET_CODE), length: 32 }), InputProps: {
                                    endAdornment: (React.createElement(React.Fragment, null,
                                        React.createElement(InputAdornment, { position: 'end' },
                                            value && React.createElement(Tooltip, { content: t('textField.clear') },
                                                React.createElement(ClickableIcon, { tabIndex: -1, accessibilityLabel: 'clear-input', icon: IconClear, onClick: () => setValue('encryptionKey', ''), className: 'icon-input' })),
                                            React.createElement(Tooltip, { content: isShowKey ? t(TOOLTIP_FIELD.SHOW, { field: t(FIELD_NAME.SECRET_CODE) }) : t(TOOLTIP_FIELD.HIDE, { field: t(FIELD_NAME.SECRET_CODE) }) },
                                                React.createElement(ClickableIcon, { tabIndex: -1, accessibilityLabel: 'show-hide-password', icon: isShowKey ? IconEyeShow : IconEyeHide, onClick: () => setIsShowKey(prev => !prev), className: 'icon-input' })))))
                                } }) })) }),
                    React.createElement(Controller, { control: control, name: 'reEncryptionKey', rules: {
                            required: VALIDATE_CODE.REQUIRED,
                            validate: (value) => {
                                const _error = validateDifferent(getValues('encryptionKey'), value);
                                return _error === VALIDATE_CODE.OK || _error;
                            }
                        }, render: ({ field: { onChange, onBlur, value } }) => (React.createElement(FormControlLabel, { label: t('recording.encryption.reKey'), required: true, labelPlacement: 'top', sx: { mt: '16px !important' }, control: React.createElement(TextField, { fullWidth: true, value: value, onChange: onChange, placeholder: t('recording.encryption.placeholderRekey'), onKeyPress: (e) => e.key === 'Enter' && e.preventDefault(), type: isShowReKey ? 'text' : 'password', onBlur: onBlur, error: errors.reEncryptionKey?.message !== VALIDATE_CODE.OK, helperText: errors.reEncryptionKey?.message && t(errors.reEncryptionKey?.message, { field: t(FIELD_NAME.SECRET_CODE) }), InputProps: {
                                    endAdornment: (React.createElement(React.Fragment, null,
                                        React.createElement(InputAdornment, { position: 'end', tabIndex: -1 },
                                            value && React.createElement(Tooltip, { content: t('textField.clear') },
                                                React.createElement(ClickableIcon, { tabIndex: -1, accessibilityLabel: 'clear-input', icon: IconClear, onClick: () => setValue('reEncryptionKey', ''), className: 'icon-input' })),
                                            React.createElement(Tooltip, { content: isShowKey ? t(TOOLTIP_FIELD.SHOW, { field: t(FIELD_NAME.SECRET_CODE) }) : t(TOOLTIP_FIELD.HIDE, { field: t(FIELD_NAME.SECRET_CODE) }) },
                                                React.createElement(ClickableIcon, { tabIndex: -1, accessibilityLabel: 'show-hide-password', icon: isShowReKey ? IconEyeShow : IconEyeHide, onClick: () => setIsShowReKey(prev => !prev), className: 'icon-input' })))))
                                } }) })) })))));
};
export default StartRecordingDialogV3;
